import Box from '@mui/material/Box'
import FormControlLabel from '@mui/material/FormControlLabel'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { startOfDay } from 'date-fns'
import React, { useCallback } from 'react'

import HHDesktopDatePicker from 'components/form-fields/v5/HHDesktopDatePicker'
import T from 'T'
import { isNotInvalidDate } from 'utils/date'

export type ReactivationTiming = 'NOW' | 'SCHEDULED'

type ReactivationWhenProps = {
  timing: ReactivationTiming
  effectiveDate: Date | null
  effectiveDateError?: string
  dateFieldRef?: React.Ref<HTMLDivElement>
  onTimingChange: (timing: ReactivationTiming) => void
  onEffectiveDateChange: (date: Date | null) => void
}

const LABEL_ID = 'reactivation-when-label'

// The server judges by the tenant's day; a typed date skips the calendar's limit and arrives invalid while incomplete.
export const isValidScheduleDate = (date: Date) => isNotInvalidDate(date) && date > startOfDay(new Date())

const isNotSchedulable = (date: Date) => !isValidScheduleDate(date)

const ReactivationWhen = ({
  timing,
  effectiveDate,
  effectiveDateError,
  dateFieldRef,
  onTimingChange,
  onEffectiveDateChange,
}: ReactivationWhenProps) => {
  const handleTimingChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => onTimingChange(event.target.value as ReactivationTiming),
    [onTimingChange]
  )

  return (
    <Stack alignItems="center" columnGap={2} direction="row" flexWrap="wrap" rowGap={1}>
      <Typography fontWeight={600} id={LABEL_ID} variant="body2">
        {T.WHEN}
      </Typography>
      <RadioGroup row aria-labelledby={LABEL_ID} value={timing} onChange={handleTimingChange}>
        <FormControlLabel control={<Radio size="small" />} label={<Typography variant="body2">{T.IMMEDIATELY}</Typography>} value="NOW" />
        <FormControlLabel
          control={<Radio size="small" />}
          label={<Typography variant="body2">{T.ON_A_START_DATE}</Typography>}
          value="SCHEDULED"
        />
      </RadioGroup>
      {timing === 'SCHEDULED' && (
        <Box ref={dateFieldRef}>
          <HHDesktopDatePicker
            disableScrollLock
            label={T.START_DATE}
            shouldDisableDate={isNotSchedulable}
            TextFieldProps={{
              required: true,
              size: 'small',
              fullWidth: false,
              sx: { width: 200 },
              error: Boolean(effectiveDateError),
              helperText: effectiveDateError,
            }}
            value={effectiveDate}
            onChange={onEffectiveDateChange}
          />
        </Box>
      )}
    </Stack>
  )
}

export default ReactivationWhen
